export const useMoney = () => {
  const getCompactMoneyFormat = (amount: number, currency?: string): string => {
    const absAmount = Math.abs(amount);
    const formattedAmount: { amount: number | string; suffix: string } = { amount: 0, suffix: '' };

    if (absAmount < 100 * 1000) {
      formattedAmount.amount = amount;
    } else if (absAmount < 1000 * 1000) {
      formattedAmount.amount = (amount / 1000).toFixed(1);
      formattedAmount.suffix = 'k';
    } else {
      formattedAmount.amount = (amount / 1000000).toFixed(1);
      formattedAmount.suffix = 'M';
    }

    const formatter = new Intl.NumberFormat('cs', { style: 'decimal' });
    const value = (formatter.format(Number(formattedAmount.amount)) + formattedAmount.suffix).replaceAll(`\xA0`, ' ');

    return currency ? `${value} ${currency}` : value;
  };

  const formatAmount = (
    amount: number,
    currency?: string,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  ): string => {
    const formatter = new Intl.NumberFormat('cs', { style: 'decimal', minimumFractionDigits, maximumFractionDigits });
    const value = formatter.format(amount);

    return currency ? `${value} ${currency}` : value;
  };

  /** Default money formatting: a missing or zero amount collapses to a dash to keep tables quiet. */
  const getMoneyFormat = (amount: number, currency?: string, minimumFractionDigits = 2, maximumFractionDigits = 2) => {
    if (!amount) {
      return '-';
    }
    return formatAmount(amount, currency, minimumFractionDigits, maximumFractionDigits);
  };

  /**
   * Same formatting, but a zero is spelled out instead of collapsing to a dash.
   *
   * Use it where zero is a real value rather than "no data" — e.g. a remaining budget of 0 is exactly
   * the state that blocks any further allocation, so a dash would hide the one number that matters.
   */
  const getExactMoneyFormat = (
    amount: number,
    currency?: string,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  ): string => formatAmount(amount ?? 0, currency, minimumFractionDigits, maximumFractionDigits);

  const roundPrice = (number: number) => {
    return Math.round((number + Number.EPSILON) * 100) / 100;
  };

  return {
    getCompactMoneyFormat,
    getMoneyFormat,
    getExactMoneyFormat,
    roundPrice,
  };
};
